import React, { useState, useEffect } from 'react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';
import { Link } from 'react-router-dom';

const FeaturesSection = () => {
  const [alertCount, setAlertCount] = useState(3);
  const [selectedPolicy, setSelectedPolicy] = useState('current');
  const [chartData, setChartData] = useState([45, 52, 48, 61, 58, 67]);
  const [trackingCount, setTrackingCount] = useState(100247);

  // Simulate real-time alert updates
  useEffect(() => {
    const interval = setInterval(() => {
      setAlertCount(prev => prev + 1);
    }, 8000);
    return () => clearInterval(interval);
  }, []);

  // Simulate policy tracking counter
  useEffect(() => {
    const interval = setInterval(() => {
      setTrackingCount(prev => prev + Math.floor(Math.random() * 3));
    }, 5000);
    return () => clearInterval(interval);
  }, []);

  const features = [
    {
      id: 'alerts',
      title: "Email Alerts",
      subtitle: "Live Alert Simulation",
      component: (
        <div className="space-y-3">
          <div className="flex items-center justify-between mb-4">
            <span className="text-xs font-medium text-gray-500">INBOX</span>
            <div className="flex items-center gap-2 bg-[#ED1C24]/10 px-3 py-1 rounded-full">
              <div className="w-2 h-2 bg-[#ED1C24] rounded-full animate-pulse"></div>
              <span className="text-xs font-semibold text-[#ED1C24]">{alertCount} New</span>
            </div>
          </div>
          {[
            { payer: 'UnitedHealthcare', code: 'CPT 64555', time: '2m', priority: 'high' },
            { payer: 'Aetna', code: 'Prior Auth Update', time: '8m', priority: 'medium' },
            { payer: 'Medicare Part B', code: 'LCD Revision', time: '15m', priority: 'high' }
          ]?.map((alert, idx) => (
            <div key={idx} className="bg-white border border-gray-200 rounded-lg p-3 hover:border-[#1A73E8] transition-all cursor-pointer group">
              <div className="flex items-start gap-3">
                <div className={`w-2 h-2 rounded-full mt-1.5 ${alert?.priority === 'high' ? 'bg-[#ED1C24]' : 'bg-[#F59E0B]'}`}></div>
                <div className="flex-1 min-w-0">
                  <p className="text-sm font-semibold text-[#1B1B20] truncate">{alert?.payer}</p>
                  <p className="text-xs text-gray-600 mt-0.5">{alert?.code}</p>
                </div>
                <span className="text-xs text-gray-400 flex-shrink-0">{alert?.time}</span>
              </div>
            </div>
          ))}
        </div>
      )
    },
    {
      id: 'reports',
      title: "Coverage Reports",
      subtitle: "Interactive Document Viewer",
      component: (
        <div className="space-y-3">
          <div className="flex gap-2 mb-4">
            {['Active', 'Draft', 'Pending']?.map((status) => (
              <button
                key={status}
                className={`px-3 py-1.5 text-xs font-medium rounded-lg transition-all ${
                  status === 'Active' ?'bg-[#1A73E8] text-white' :'bg-gray-100 text-gray-600 hover:bg-gray-200'
                }`}
              >
                {status}
              </button>
            ))}
          </div>
          <div className="space-y-2">
            {[
              { title: 'Medical Policy - Spinal Procedures', pages: 24, updated: 'Today' },
              { title: 'Coverage Statement - Orthopedic Devices', pages: 18, updated: '2 days ago' },
              { title: 'Prior Authorization Guidelines', pages: 32, updated: '1 week ago' }
            ]?.map((doc, idx) => (
              <div key={idx} className="bg-gradient-to-r from-gray-50 to-white border border-gray-200 rounded-lg p-3 hover:shadow-md transition-all cursor-pointer group">
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 bg-[#1A73E8]/10 rounded-lg flex items-center justify-center group-hover:bg-[#1A73E8]/20 transition-colors">
                    <Icon name="FileText" size={20} color="#1A73E8" />
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-semibold text-[#1B1B20] truncate">{doc?.title}</p>
                    <p className="text-xs text-gray-500">{doc?.pages} pages • Updated {doc?.updated}</p>
                  </div>
                  <Icon name="ChevronRight" size={16} color="#9CA3AF" />
                </div>
              </div>
            ))}
          </div>
        </div>
      )
    },
    {
      id: 'analytics',
      title: "Analytics Dashboard",
      subtitle: "Live Policy Trends",
      component: (
        <div className="space-y-4">
          <div className="flex items-center justify-between">
            <span className="text-xs font-medium text-gray-500">POLICY CHANGES - LAST 6 MONTHS</span>
            <div className="flex items-center gap-1">
              <Icon name="TrendingUp" size={14} color="#10B981" />
              <span className="text-xs font-semibold text-[#10B981]">+23%</span>
            </div>
          </div>
          <div className="flex items-end justify-between h-32 gap-2 bg-white rounded-lg p-3">
            {chartData?.map((value, idx) => (
              <div key={idx} className="flex-1 flex flex-col items-center gap-2">
                <div 
                  className="w-full bg-gradient-to-t from-[#1A73E8] to-[#1A73E8]/60 rounded-t-lg hover:from-[#ED1C24] hover:to-[#ED1C24]/60 transition-all cursor-pointer" 
                  style={{ height: `${value}%`, minHeight: '20px' }}
                ></div>
                <span className="text-xs text-gray-400">{['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun']?.[idx]}</span>
              </div>
            ))}
          </div>
          <div className="grid grid-cols-3 gap-3 pt-3 border-t border-gray-200">
            <div className="text-center">
              <p className="text-lg font-bold text-[#1A73E8]">{chartData?.[chartData?.length - 1] * 13}</p>
              <p className="text-xs text-gray-500">This Month</p>
            </div>
            <div className="text-center">
              <p className="text-lg font-bold text-[#10B981]">23%</p>
              <p className="text-xs text-gray-500">Growth</p>
            </div>
            <div className="text-center">
              <p className="text-lg font-bold text-[#F59E0B]">142</p>
              <p className="text-xs text-gray-500">High Impact</p>
            </div>
          </div>
        </div>
      )
    },
    {
      id: 'tracking',
      title: "Policy Tracking",
      subtitle: "Real-Time Monitoring",
      component: (
        <div className="space-y-4">
          <div className="bg-gradient-to-br from-[#1B1B20] to-[#2A2A30] rounded-xl p-4 text-white">
            <div className="flex items-center justify-between mb-3">
              <span className="text-xs font-medium text-gray-400">POLICIES MONITORED</span>
              <div className="flex items-center gap-1.5 bg-green-500/20 px-2 py-1 rounded-full">
                <div className="w-1.5 h-1.5 bg-green-500 rounded-full animate-pulse"></div>
                <span className="text-xs font-medium text-green-400">Live</span>
              </div>
            </div>
            <p className="text-3xl font-bold mb-4">{trackingCount?.toLocaleString()}</p>
            <div className="space-y-2">
              {[
                { payer: 'Medicare', count: 34521, color: '#1A73E8' },
                { payer: 'Medicaid', count: 28934, color: '#10B981' },
                { payer: 'Commercial', count: 36792, color: '#F59E0B' }
              ]?.map((item, idx) => (
                <div key={idx} className="flex items-center justify-between">
                  <div className="flex items-center gap-2">
                    <div className="w-2 h-2 rounded-full" style={{ backgroundColor: item?.color }}></div>
                    <span className="text-sm text-gray-300">{item?.payer}</span>
                  </div>
                  <span className="text-sm font-semibold">{item?.count?.toLocaleString()}</span>
                </div>
              ))}
            </div>
          </div>
          <Link 
            to="/request-demo" 
            className="block text-center text-sm text-[#1A73E8] hover:text-[#ED1C24] font-medium transition-colors hover:underline"
          >
            Click here to view all policies →
          </Link>
        </div>
      )
    },
    {
      id: 'analysis',
      title: "Policy Analysis",
      subtitle: "Side-by-Side Comparison",
      component: (
        <div className="space-y-3 h-full flex flex-col">
          <div className="flex gap-2 mb-3">
            <button
              onClick={() => setSelectedPolicy('current')}
              className={`flex-1 px-3 py-2.5 text-xs font-medium rounded-lg transition-all relative overflow-hidden ${
                selectedPolicy === 'current' ?'bg-[#1A73E8] text-white shadow-lg' :'bg-gray-100 text-gray-600 hover:bg-gray-200'
              }`}
            >
              <div className="flex flex-col items-start">
                <span className="font-semibold">Current Version</span>
                <span className={`text-[10px] mt-0.5 ${
                  selectedPolicy === 'current' ? 'text-blue-100' : 'text-gray-500'
                }`}>v2.3 • Jan 15, 2026</span>
              </div>
              {selectedPolicy === 'current' && (
                <div className="absolute top-1 right-1">
                  <div className="w-2 h-2 bg-[#10B981] rounded-full animate-pulse"></div>
                </div>
              )}
            </button>
            <button
              onClick={() => setSelectedPolicy('previous')}
              className={`flex-1 px-3 py-2.5 text-xs font-medium rounded-lg transition-all relative ${
                selectedPolicy === 'previous' ?'bg-[#1A73E8] text-white shadow-lg' :'bg-gray-100 text-gray-600 hover:bg-gray-200'
              }`}
            >
              <div className="flex flex-col items-start">
                <span className="font-semibold">Previous Version</span>
                <span className={`text-[10px] mt-0.5 ${
                  selectedPolicy === 'previous' ? 'text-blue-100' : 'text-gray-500'
                }`}>v2.2 • Dec 1, 2025</span>
              </div>
            </button>
          </div>
          
          <div className="bg-gradient-to-br from-blue-50 to-white border border-blue-200 rounded-lg p-3 mb-3">
            <div className="flex items-center justify-between text-xs">
              <div className="flex items-center gap-2">
                <Icon name="FileText" size={14} className="text-[#1A73E8]" />
                <span className="font-medium text-gray-700">Medicare Part B - Surgical Procedures</span>
              </div>
              <div className="flex items-center gap-1 text-[#ED1C24] font-semibold">
                <Icon name="AlertCircle" size={12} />
                <span>2 Changes</span>
              </div>
            </div>
          </div>

          <div className="bg-white border border-gray-200 rounded-lg p-4 space-y-3 flex-1">
            <div className="flex items-start gap-2 transform transition-all hover:scale-[1.02] hover:shadow-md rounded-lg p-2 -m-2">
              <div className="w-1 h-12 bg-[#10B981] rounded-full flex-shrink-0 shadow-sm"></div>
              <div className="flex-1">
                <div className="flex items-center gap-2 mb-1">
                  <p className="text-xs font-semibold text-[#10B981]">ADDED</p>
                  <span className="text-[10px] text-gray-400">Section 4.2</span>
                </div>
                <p className="text-sm text-gray-700 leading-relaxed">Prior authorization required for procedures exceeding $5,000</p>
                <div className="mt-2 flex items-center gap-2">
                  <span className="text-[10px] bg-green-50 text-green-700 px-2 py-0.5 rounded-full font-medium">High Impact</span>
                </div>
              </div>
            </div>
            
            <div className="flex items-start gap-2 transform transition-all hover:scale-[1.02] hover:shadow-md rounded-lg p-2 -m-2">
              <div className="w-1 h-12 bg-[#F59E0B] rounded-full flex-shrink-0 shadow-sm"></div>
              <div className="flex-1">
                <div className="flex items-center gap-2 mb-1">
                  <p className="text-xs font-semibold text-[#F59E0B]">MODIFIED</p>
                  <span className="text-[10px] text-gray-400">Section 5.4</span>
                </div>
                <p className="text-sm text-gray-700 leading-relaxed">
                  Coverage criteria updated for CPT codes 64550-64555
                </p>
                <div className="mt-2 flex items-center gap-2">
                  <span className="text-[10px] bg-amber-50 text-amber-700 px-2 py-0.5 rounded-full font-medium">Medium Impact</span>
                </div>
              </div>
            </div>
          </div>
          
          <div className="flex items-center justify-between text-xs text-gray-500 pt-2">
            <span>Last compared: 2 minutes ago</span>
            <button className="text-[#1A73E8] hover:underline font-medium flex items-center gap-1">
              <Icon name="Download" size={12} />
              Export
            </button>
          </div>
        </div>
      )
    },
    {
      id: 'collaboration',
      title: "Collaborative Tools",
      subtitle: "Team Workspace",
      component: (
        <div className="space-y-3 h-full flex flex-col">
          <div className="flex items-center gap-2 mb-4">
            <div className="flex -space-x-2">
              {[1, 2, 3, 4]?.map((i) => (
                <div key={i} className="w-8 h-8 rounded-full bg-gradient-to-br from-[#1A73E8] to-[#ED1C24] border-2 border-white flex items-center justify-center text-white text-xs font-semibold">
                  {String.fromCharCode(64 + i)}
                </div>
              ))}
            </div>
            <span className="text-xs text-gray-500">4 team members active</span>
          </div>
          <div className="space-y-2 flex-1">
            {[
              { action: 'shared', user: 'Sarah M.', item: 'Q1 Coverage Report', time: '5m ago', icon: 'Share2' },
              { action: 'exported', user: 'James C.', item: 'Policy Analysis to Excel', time: '12m ago', icon: 'Download' },
              { action: 'saved', user: 'Michael R.', item: 'Custom Analytics Chart', time: '28m ago', icon: 'Save' }
            ]?.map((activity, idx) => (
              <div key={idx} className="bg-gradient-to-r from-gray-50 to-white border border-gray-200 rounded-lg p-3 hover:border-[#1A73E8] transition-all">
                <div className="flex items-center gap-3">
                  <div className="w-8 h-8 bg-[#1A73E8]/10 rounded-lg flex items-center justify-center flex-shrink-0">
                    <Icon name={activity?.icon} size={16} color="#1A73E8" />
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="text-sm text-gray-700">
                      <span className="font-semibold text-[#1B1B20]">{activity?.user}</span> {activity?.action} <span className="font-medium">{activity?.item}</span>
                    </p>
                    <p className="text-xs text-gray-500">{activity?.time}</p>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      )
    }
  ];

  return (
    <section className="bg-gradient-to-b from-gray-50 to-white py-16 md:py-20 lg:py-28">
      <div className="max-w-7xl mx-auto px-4 md:px-6 lg:px-8">
        <div className="text-center mb-12 md:mb-16 lg:mb-20">
          <div className="inline-flex items-center gap-2 px-3 py-1.5 md:px-4 md:py-2 bg-[#1A73E8]/10 rounded-full mb-4 md:mb-6">
            <Icon name="Zap" size={16} color="#1A73E8" />
            <span className="text-xs md:text-sm font-medium text-[#1A73E8]">Platform Features</span>
          </div>
          <h2 className="text-3xl md:text-4xl lg:text-5xl font-bold text-[#1B1B20] mb-4 md:mb-6">
            See PolicyAlerts in Action
          </h2>
          <p className="text-base md:text-lg lg:text-xl text-gray-600 max-w-3xl mx-auto">
            Interactive previews of our platform capabilities—not just descriptions, but actual demonstrations of how PolicyAlerts works.
          </p>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 md:gap-8 mb-12 md:mb-16">
          {features?.map((feature, idx) => (
            <div 
              key={idx}
              className="group p-6 md:p-8 bg-white rounded-2xl shadow-sm hover:shadow-2xl transition-all duration-300 border border-gray-100 hover:border-[#1A73E8]/30 flex flex-col"
            >
              <div className="mb-4 md:mb-6">
                <h3 className="text-lg md:text-xl font-bold text-[#1B1B20] mb-1">
                  {feature?.title}
                </h3>
                <p className="text-xs md:text-sm text-[#1A73E8] font-medium">
                  {feature?.subtitle}
                </p>
              </div>
              <div className="bg-gray-50 rounded-xl p-4 flex-1 flex flex-col">
                {feature?.component}
              </div>
            </div>
          ))}
        </div>

        <div className="bg-gradient-to-br from-[#1B1B20] to-[#0A0A0A] rounded-2xl p-8 md:p-12 lg:p-16 text-center text-white">
          <h3 className="text-2xl md:text-3xl lg:text-4xl font-bold mb-4 md:mb-6">
            Experience the Full Platform
          </h3>
          <p className="text-base md:text-lg text-gray-300 mb-6 md:mb-8 max-w-2xl mx-auto">
            These interactive previews are just the beginning. See the complete PolicyAlerts platform with our comprehensive demonstration.
          </p>
          <div className="flex flex-col sm:flex-row gap-3 md:gap-4 justify-center">
            <Button 
              variant="default" 
              size="lg"
              iconName="Play"
              iconPosition="left"
              className="bg-[#ED1C24] hover:bg-[#ED1C24]/90"
              asChild
            >
              <Link to="/platform-demo">Watch Demo</Link>
            </Button>
            <Button 
              variant="outline" 
              size="lg"
              iconName="Calendar"
              iconPosition="left"
              className="border-white/30 text-white hover:bg-white/10"
              asChild
            >
              <Link to="/request-demo">Request Demo</Link>
            </Button>
          </div>
        </div>
      </div>
    </section>
  );
};

export default FeaturesSection;